import type { ComponentProps } from 'react'
import React, { useCallback, useContext, useRef, useMemo, useImperativeHandle, forwardRef } from 'react'
import { useIntl } from 'react-intl'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useToggle from 'hooks/useToggle'
import FormattedCruiseLineSearchSummary from '../CruiseLineSelect/FormattedCruiseLineSearchSummary'
import CruiseWhenSelectContent from './CruiseWhenSelectContent'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'

import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

export interface CruiseWhenSelectDesktopRef {
  focus: () => void
}

interface Props {
  inputValue: string
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef: React.RefObject<HTMLElement | null>
  isChipMode?: boolean
  calendarSize?: 'S' | 'M'
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  initialFilters?: App.CruiseInitialFilters
  shouldIgnoreFlashOffers?: boolean
}

const CruiseWhenSelectDesktop = forwardRef<CruiseWhenSelectDesktopRef, Props>(({
  inputValue,
  onChange,
  dropdownAnchorRef,
  isChipMode,
  calendarSize,
  dropdownSize,
  initialFilters,
  shouldIgnoreFlashOffers,
}, ref) => {
  const intl = useIntl()
  const [isOpen, toggleModal, , closeModal] = useToggle(false)
  const globalSearchState = useContext(GlobalSearchStateContext)
  const { flexibleMonths, durationRange, durationMax, durationMin, checkinDate, checkoutDate } = globalSearchState
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const onApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_when_filter',
      type: 'interaction',
    })

    const flexibleMonthsLabel = intl.formatMessage({
      id: 'xV3QfiEukhgItwBGd3l3',
      defaultMessage: 'Flexible Months',
      description: 'Label for flexible months filter context in the filters panel',
    })
    const durationRangeLabel = intl.formatMessage({
      id: '7XvoBcN_iszHgaGJn1-C',
      defaultMessage: 'Duration Range',
      description: 'Label for duration range filter context in the search or listing filters panel',
    })
    const checkinDateLabel = intl.formatMessage({
      id: 'eLDbq30q055v_WTkGHl9',
      defaultMessage: 'Check-in Date',
      description: 'Label for check-in date filter context in the search or listing filters panel',
    })
    const checkoutDateLabel = intl.formatMessage({
      id: 'wNib6clO_fegr8pekY-F',
      defaultMessage: 'Check-out Date',
      description: 'Label for check-out date filter context in the search or listing filters panel',
    })

    const filterContexts: Array<{ label: string; type: string; value: string | number }> = []
    if (flexibleMonths) {
      filterContexts.push({ label: flexibleMonthsLabel, type: 'flexible_months', value: flexibleMonths })
    }
    if (durationRange?.length) {
      filterContexts.push({ label: durationRangeLabel, type: 'duration_range', value: durationRange.join(', ') })
    }
    if (checkinDate) {
      filterContexts.push({ label: checkinDateLabel, type: 'checkin_date', value: checkinDate.format(DMY_CASUAL_FORMAT) })
    }
    if (checkoutDate) {
      filterContexts.push({ label: checkoutDateLabel, type: 'checkout_date', value: checkoutDate.format(DMY_CASUAL_FORMAT) })
    }

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts,
    }))

    onChange({
      flexibleMonths,
      durationMax,
      durationMin,
      checkinDate,
      checkoutDate,
      durationRange,
    })
    closeModal()
  }, [onChange, flexibleMonths, durationMax, durationMin, checkinDate, checkoutDate, durationRange, closeModal, globalSearchState, intl])

  const onClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_when_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: undefined, max: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: '' })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE, durationRange: [] })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: undefined })
  }, [searchDispatch])

  const hasSelectedData = useMemo(() => {
    return !!flexibleMonths?.length || !!durationMax || !!durationMin || !!checkinDate || !!checkoutDate
  }, [flexibleMonths, durationMax, durationMin, checkinDate, checkoutDate])

  const whenLabel = intl.formatMessage({
    id: 'q2qv5ezlrc4s_Z9JYS4N',
    defaultMessage: 'When',
    description: 'Label for the date range filter input in the search and discovery interface',
  })

  const anytimePlaceholder = intl.formatMessage({
    id: 'cdSrWYoX2bEz81_M8hIY',
    defaultMessage: 'Anytime',
    description: 'Placeholder text for when filter input in the search or listing filters panel',
  })

  const applyButtonText = intl.formatMessage({
    id: 'd_HKrG0iRO6IZORXnx18',
    defaultMessage: 'Apply',
    description: 'Apply button text for when filter in the search or listing filters panel',
  })

  const resetAllButtonText = intl.formatMessage({
    id: 'tIMSqM2vkzAAzcDSAX2P',
    defaultMessage: 'Reset all',
    description: 'Reset all button text for when filter in the search or listing filters panel',
  })

  const formFieldRef = useRef<HTMLButtonElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      formFieldRef.current?.focus()
      toggleModal()
    },
  }), [toggleModal])

  return <>
    <CruiseFilterInput
      isOpen={isOpen}
      label={whenLabel}
      placeholder={anytimePlaceholder}
      inputValue={inputValue}
      data-testid="cruise-when-select-input"
      formFieldRef={formFieldRef}
      onClick={() => {
        Analytics.trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_when_filter',
          type: 'interaction',
        })
        toggleModal()
      }}
      isChipMode={isChipMode}
      hasSelectedData={hasSelectedData}
    />

    <DropdownSheet
      data-testid="cruise-when-select-dropdown"
      anchorRef={dropdownAnchorRef}
      size={dropdownSize || 'fill-anchor'}
      triggerRef={formFieldRef}
      open={isOpen}
      onClose={closeModal}
      title={isChipMode ? whenLabel : undefined}
      hasDismissButton={isChipMode}
      primaryActionProps={{
        'data-testid': 'cruise-filter-footer-apply',
        children: applyButtonText,
        onClick: onApply,
      }}
      secondaryActionProps={{
        'data-testid': 'cruise-filter-footer-clear-all',
        kind: 'tertiary',
        children: resetAllButtonText,
        onClick: onClear,
      }}
      footerStart={<FormattedCruiseLineSearchSummary
        initialFilters={initialFilters}
        shouldIgnoreFlashOffers={shouldIgnoreFlashOffers}
      />}
    >
      <CruiseWhenSelectContent onChange={onChange} calendarSize={calendarSize} initialFilters={initialFilters} />
    </DropdownSheet>
  </>
})

CruiseWhenSelectDesktop.displayName = 'CruiseWhenSelectDesktop'

export default CruiseWhenSelectDesktop
